import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, Receipt, FileText, Send, MonitorSmartphone } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { InviteFormButton } from "@/components/InviteFormDialog";
import { FormInvitesLog } from "@/components/FormInvitesLog";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { ClientDialog } from "@/components/ClientDialog";
import { PhoneLinks } from "@/components/PhoneLinks";

export default function Clients() {
  const [clients, setClients] = useState(null);
  const [q, setQ] = useState("");
  const { user } = useAuth();
  const canImpersonate = user?.role === "admin" && !user.impersonator;
  const openPortal = async (c) => {
    const tab = window.open("about:blank", "_blank");
    try { await api.post(`/admin/clients/${c.id}/portal-session`); if (tab) tab.location.href = "/portal"; else window.location.href = "/portal"; }
    catch (e) { tab?.close(); toast.error(errMsg(e, "Could not open portal")); }
  };
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/clients").then((r) => setClients(r.data));
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      if (editing?.id) await api.put(`/clients/${editing.id}`, data);
      else await api.post("/clients", data);
      toast.success("Client saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(errMsg(e, "Could not save client")); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name}? Their unpaid invoices are removed and studies unlinked.`)) return;
    try { await api.delete(`/clients/${c.id}`); toast.success("Client deleted"); load(); }
    catch (e) { toast.error(errMsg(e, "Could not delete client")); }
  };
  const invite = async (c) => {
    try { const r = await api.post(`/clients/${c.id}/portal-invite`); toast.success(`Portal invite sent to ${r.data.sent_to}`); load(); }
    catch (e) { toast.error(errMsg(e, "Could not send invite")); }
  };
  const list = (clients || []).filter((c) => `${c.name} ${c.company} ${c.email}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-2">Customers</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Clients</h1>
        </div>
        <div className="flex flex-wrap gap-3 w-full sm:w-auto">
          <div className="relative w-full sm:w-64">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input data-testid="clients-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients" className="pl-9 bg-slate-900 border-slate-700" />
          </div>
          <InviteFormButton />
          <Button data-testid="new-client-btn" onClick={() => setEditing({})} className="rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400"><Plus className="h-4 w-4 mr-1" />New client</Button>
        </div>
      </div>
      <div className="rounded-xl border border-slate-800 overflow-x-auto" data-testid="clients-table">
        <table className="w-full text-sm m-cards">
          <thead className="bg-slate-950/60 text-xs uppercase tracking-wider text-slate-500">
            <tr>{["Client", "Email / phone", "Studies", "Invoices", ""].map((h) => <th key={h} className="text-left px-5 py-3 font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.id} className="border-t border-slate-800 hover:bg-slate-900/60" data-testid={`client-row-${c.id}`}>
                <td className="px-5 py-4"><p className="font-semibold text-slate-100">{c.name}</p><p className="text-xs text-slate-500">{c.company}</p>{c.portal_invited_at && <p className="text-[10px] text-emerald-400 mt-0.5">Portal invited {c.portal_invited_at.slice(0, 10)}</p>}</td>
                <td data-label="Email / phone" className="px-5 py-4 text-slate-400 break-all"><p>{c.email}</p><PhoneLinks phone={c.phone} testId={`client-phone-${c.id}`} className="text-xs mt-1" /></td>
                <td data-label="Studies" className="px-5 py-4"><Link to={`/studies?client=${c.id}`} className="inline-flex items-center gap-1 text-slate-300 hover:text-emerald-400"><FileText className="h-4 w-4" /><span className="num">{c.study_count}</span></Link></td>
                <td data-label="Invoices" className="px-5 py-4"><Link to={`/invoices?client=${c.id}`} className="inline-flex items-center gap-1 text-slate-300 hover:text-emerald-400"><Receipt className="h-4 w-4" /><span className="num">{c.invoice_count}</span></Link></td>
                <td className="px-5 py-4 text-right whitespace-nowrap max-md:flex max-md:flex-wrap max-md:gap-1">
                  <Button size="icon" variant="ghost" title={c.email ? "Email portal invite" : "Add an email to invite"} disabled={!c.email} data-testid={`invite-client-${c.id}`} className="hover:text-emerald-400" onClick={() => invite(c)}><Send className="h-4 w-4" /></Button>
                  {canImpersonate && <Button size="sm" variant="outline" title="Open this client's portal for up to 1 hour" data-testid={`portal-as-client-${c.id}`} onClick={() => openPortal(c)} className="h-8 mr-1 rounded-full border-amber-400/40 bg-transparent text-amber-300 hover:bg-amber-400/10"><MonitorSmartphone className="h-4 w-4 mr-1" />Open portal as client</Button>}
                  <Button size="icon" variant="ghost" data-testid={`edit-client-${c.id}`} onClick={() => setEditing(c)}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" data-testid={`delete-client-${c.id}`} className="hover:text-rose-400" onClick={() => remove(c)}><Trash2 className="h-4 w-4" /></Button>
                </td>
              </tr>
            ))}
            {clients && list.length === 0 && <tr><td colSpan={5} className="px-5 py-12 text-center text-slate-500" data-testid="clients-empty">{q ? "No matching clients." : "No clients yet — add your first paying customer."}</td></tr>}
          </tbody>
        </table>
      </div>
      <FormInvitesLog />
      <ClientDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} initial={editing} onSubmit={save} />
    </div>
  );
}
